<template>
  <div class="lib__form__main">
    <ValidationObserver ref="validator" slim tag="div" class="lib__form__main__content">
      <header class="lib__form__main__header">
        <h2 class="lib__h2">{{ $t('new-connection-cmh.offering-selection-step.whichConnectionDoYouWant') }}</h2>
      </header>

      <cmh-offerings-selector :isCmhAvailable="stateSubCity.connectMyHomeActivated" />

      <div v-if="isGdprDisplayed" class="lib__field lib__field--checkbox lib__field--border">
        <label class="lib__field__label">{{ $t('new-connection-cmh.offering-selection-step.privateLifeNotice')
          }}</label>
        <div class="lib__field__note">
          {{ $t('new-connection-cmh.offering-selection-step.privateLifeNoticeLinks') }}
          <template v-if="isProximusAvailable">
            <a v-if="isProximusAvailable" :href="$t('new-connection-cmh.links.proximus-privacy')"
              class="private-life-notice-link" target="_blank">{{ $t('operator.proximus') }}</a>
          </template>
          <template v-if="isSwdeAvailable">
            {{ ' - ' }}<a v-if="isSwdeAvailable" :href="$t('new-connection-cmh.links.swde-privacy')" target="_blank"
              class="private-life-notice-link">{{ $t('operator.swde') }}</a>
          </template>
          <template v-if="isOrangeAvailable">
            {{ ' - ' }}<a v-if="isOrangeAvailable" :href="$t('new-connection-cmh.links.orange-privacy')" target="_blank"
              class="private-life-notice-link">{{ $t('operator.orange') }}</a>
          </template>
          <template v-if="isGasManagerResa">
            {{ ' - ' }}<a v-if="isGasManagerResa" :href="$t('new-connection-cmh.links.resa-privacy')" target="_blank"
              class="private-life-notice-link">{{ $t('operator.resa') }}</a>
          </template>
        </div>
      </div>

    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import Step from '@/components/steps/Step'
import Component from 'vue-class-component'
import { CmhAddressPoint } from '@/models/cmh-address-point'
import { State, Mutation, Getter } from 'vuex-class'
import { helper } from '@/services'
import CmhOfferingsSelector from '@/components/inputs/CmhOfferings/CmhOfferingsSelector.vue'
import { OfferingSelection, OfferingSelectionOperator, SubCity } from '@/models'
import { pixelFacebookService } from '@ores/vue-library'
import { MutationType } from '@/store/new-connection-cmh/offerings-selection/mutations'

@Component({
  components: {
    CmhOfferingsSelector
  },
  name: 'cmh-offerings-selection-step'
})
export default class CmhOfferingsSelectionStep extends Step {
  //#region [Data]
  public clonedAddressPoint!: CmhAddressPoint
  public isEnergySelectorErrorDisplayed: boolean = false

  @State('subCity', { namespace: 'newConnectionCmh/landingPage' })
  public stateSubCity!: SubCity
  @State('addressPoint', { namespace: 'newConnectionCmh/cmhAddress' })
  public stateAddressPoint!: CmhAddressPoint
  @State('selections', { namespace: 'newConnectionCmh/offeringsSelection' })
  public stateSelections!: OfferingSelectionOperator[]
  @State('requestId', { namespace: 'newConnectionCmh' })
  public requestId!: string

  @Getter('isElectricityAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isElectricityAvailable!: boolean
  @Getter('isGasAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isGasAvailable!: boolean
  @Getter('isGasManagerResa', { namespace: 'newConnectionCmh/cmhAddress' })
  public isGasManagerResa!: boolean
  @Getter('isConnectMyHomeAvailableForSelectedBuilding', { namespace: 'newConnectionCmh/cmhAddress' })
  public isConnectMyHomeAvailableForSelectedBuilding!: boolean
  @Getter('isProximusAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isProximusAvailable!: boolean
  @Getter('isOrangeAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isOrangeAvailable!: boolean
  @Getter('isSwdeAvailable', { namespace: 'newConnectionCmh/cmhAddress' })
  public isSwdeAvailable!: boolean
  @Getter('isElectricitySelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isElectricitySelected!: boolean
  @Getter('isGasOresSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isGasOresSelected!: boolean
  @Getter('isGasResaSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isGasResaSelected!: boolean
  @Getter('isElectrictyOrGasSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isElectrictyOrGasSelected!: boolean
  @Getter('isSwdeSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isSwdeSelected!: boolean
  @Getter('isProximusSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isProximusSelected!: boolean
  @Getter('isOrangeSelected', { namespace: 'newConnectionCmh/offeringsSelection' })
  private isOrangeSelected!: boolean

  @Mutation(MutationType.MUTATION_REMOVE_ANY_OPERATOR, { namespace: 'newConnectionCmh/offeringsSelection' })
  private removeAny!: (payload: OfferingSelectionOperator[]) => void
  //#endregion

  //#region [Method]
  public mounted() {
    pixelFacebookService.trackViewContent({
      content_ids: ['CmhForm'],
      content_name: 'Offering Selection Step',
      content_type: 'product'
    })
    this.clonedAddressPoint = helper.clone(this.stateAddressPoint)
  }

  public async save(next: boolean, isValid: boolean): Promise<boolean | void> {
    if (next && isValid) {
      if (!this.isConnectMyHomeAvailableForSelectedBuilding || !this.stateSubCity.connectMyHomeActivated) {
        this.removeAny([
          OfferingSelectionOperator.swde,
          OfferingSelectionOperator.proximus,
          OfferingSelectionOperator.orange,
          OfferingSelectionOperator.proximusAndOrange
        ])
      }
    }

    this.saveStep()

    this.$appInsights.trackEvent('WR_NCCMH_Step2_Validated', { Segment: 'WR_NCCMH', WebRef: this.requestId })
  }

  public async saveStep() {
    const data: OfferingSelection = {
      electricity: this.isElectricitySelected,
      gas: this.isGasOresSelected,
      resa: this.isGasResaSelected,
      swde: this.isSwdeSelected,
      proximus: this.isProximusSelected,
      orange: this.isOrangeSelected,
    }

    this.$api.post<OfferingSelection>(`NewConnectionRequest/store/${this.requestId}/offering-selection`, data)
  }

  //#endregion

  //#region [Computed]

  public get IsOfferingErrorDisplayed(): boolean {
    if (!this.isEnergySelectorErrorDisplayed || !this.stateSelections) {
      return false;
    }

    if (this.isGasManagerResa) {
      return !this.isElectricitySelected
    }

    return !this.isElectrictyOrGasSelected
  }

  public get isGdprDisplayed() {
    return this.isGasResaSelected || this.stateSubCity.connectMyHomeActivated && (this.isSwdeSelected || this.isProximusSelected || this.isOrangeSelected)
  }

  //#endregion
}
</script>
